import { betBuilderToMarketType } from '@/core/lib/betBuilderTypeConverter';
import { i18n } from '@/core/lib/i18n';
import { toFixedDecimalPoint } from '@/core/lib/numberFormat';
import { formatPointAsRange, isBetBuilderHandicap, isBetBuilderOU } from '@/core/lib/oddsHelper';
import { formatLiveScore, getScore } from '@/core/oddsApi/helpers/liveScore';
import type { IEvent } from '@/interface/IEvent';
import type { Score } from '@/interface/ILiveScore';
import type { IBetBuilderLeg, IBetBuilderMarket, IBetBuilderTicket } from '@/interface/betBuilder';
import type { BetBuilderStatType } from '@/interface/enum';
import { BetBuilderMarket, BetBuilderSelectionType, BetTypeGroup, SportType } from '@/interface/enum';

const positivePointSelectionTypes = new Set([
    BetBuilderSelectionType._1,
    BetBuilderSelectionType.Over,
    BetBuilderSelectionType.Home,
]);

const supportedLiveScoreMarketTypes = new Set([
    BetBuilderMarket.SetHandicap,
    BetBuilderMarket.SetTotalCornersHandicap,
    BetBuilderMarket.SetTotalCornersOverUnder,
    BetBuilderMarket.SetTotalCornersOddEven,
    BetBuilderMarket.SetTotalCorners1x2,
]);

const MarketTypeAndBetTypeGroupMapping: Partial<Record<BetBuilderMarket, BetTypeGroup>> = {
    [BetBuilderMarket.SetHandicap]: BetTypeGroup.Main,
    [BetBuilderMarket.SetTotalCornersHandicap]: BetTypeGroup.Soccer_TotalCorner,
    [BetBuilderMarket.SetTotalCornersOverUnder]: BetTypeGroup.Soccer_TotalCorner,
    [BetBuilderMarket.SetTotalCornersOddEven]: BetTypeGroup.Soccer_TotalCorner,
    [BetBuilderMarket.SetTotalCorners1x2]: BetTypeGroup.Soccer_TotalCorner,
};

export function getLiveScoreMarketGroup(marketType: BetBuilderMarket): BetTypeGroup | null {
    if (!supportedLiveScoreMarketTypes.has(marketType)) {
        return null;
    }

    return getBetTypeGroup(marketType);
}

export function getBetTypeGroup(marketType: BetBuilderMarket): BetTypeGroup | null {
    return MarketTypeAndBetTypeGroupMapping[marketType] ?? null;
}

// Total corner markets settle against their own score, so each selection carries the score of its own market group.
export function getTicketLiveScore(ticket: IBetBuilderTicket): Score {
    const marketGroupId = getLiveScoreMarketGroup(ticket.market.marketType.id) ?? BetTypeGroup.Main;

    return getScore(ticket.match.eventResults, marketGroupId, ticket.match) ?? { home: 0, away: 0 };
}

// Live score only makes sense for live soccer markets that settle against a score, e.g. goals or total corners.
function getVisibleLiveScoreMarketGroup(event: IEvent, marketType: BetBuilderMarket): BetTypeGroup | null {
    if (!event.isLive || event.sportType !== SportType.Soccer) {
        return null;
    }

    return getLiveScoreMarketGroup(marketType);
}

export function hasBetBuilderLiveScore(event: IEvent, marketType: BetBuilderMarket): boolean {
    return getVisibleLiveScoreMarketGroup(event, marketType) !== null;
}

export function getBetBuilderLiveScoreDisplay(event: IEvent, marketType: BetBuilderMarket): string | null {
    const marketGroupId = getVisibleLiveScoreMarketGroup(event, marketType);
    if (marketGroupId === null) {
        return null;
    }

    const score = getScore(event.eventResults, marketGroupId, event);
    if (score === null) {
        return null;
    }

    return formatLiveScore(score.home, score.away, ':');
}

export function getSelectionPointForOddsDisplay(market: IBetBuilderMarket, selectionType: BetBuilderSelectionType) {
    const isHandicapOrOU = isBetBuilderHandicap(market.displayType) || isBetBuilderOU(market.displayType);
    if (!isHandicapOrOU) {
        return '';
    }

    const pointValue = isBetBuilderHandicap(market.displayType) ? convertBetBuilderHdpPoint(market.point) : market.point;

    if (isPointValueMatchingSelection(pointValue, selectionType)) {
        return formatPointAsRange(pointValue);
    }

    return '';
}

export function getSelectionPointForTicket(ticket: IBetBuilderTicket) {
    const isSetHandicap = isBetBuilderHandicap(ticket.market.displayType);
    const rawPoint = ticket.market.point;

    if (!isSetHandicap && !rawPoint) {
        return null;
    }

    if (!isSetHandicap) {
        return toFixedDecimalPoint(rawPoint, 2);
    }

    const absPoint = Math.abs(rawPoint);
    const displayPoint = isHandicapSide(ticket)
        ? -absPoint
        : absPoint;

    return toFixedDecimalPoint(displayPoint, 2);
}

export function getLegPointDisplay(leg: IBetBuilderLeg): string | null {
    if (leg.point === null) {
        return null;
    }

    return toFixedDecimalPoint(leg.point, 2);
}

export function getHandicapSide(market: IBetBuilderMarket): BetBuilderSelectionType {
    return market.point < 0
        ? BetBuilderSelectionType.Home
        : BetBuilderSelectionType.Away;
}

function isHandicapSide(ticket: IBetBuilderTicket) {
    return ticket.selection.type.id === getHandicapSide(ticket.market);
}

export function getMarketTitle(betBuilderMarketType: BetBuilderMarket, statType: BetBuilderStatType, fallback: string) {
    const marketType = betBuilderToMarketType(betBuilderMarketType, statType);

    if (!marketType) {
        return fallback;
    }

    return i18n.t(`market_type_name_${marketType}`) || fallback;
}

function isPointValueMatchingSelection(pointValue: number, selectionType: BetBuilderSelectionType): boolean {
    const isPositiveSide = positivePointSelectionTypes.has(selectionType);

    return isPositiveSide ? pointValue >= 0 : pointValue < 0;
}

function convertBetBuilderHdpPoint(point: number): number {
    return -point;
}

export function displayTotalGoalBetBuilder(selectionName: string, delimiter: string) {
    const displayName = () => {
        if (selectionName === '7-over') {
            return i18n.t('odds_option_tg_7_up');
        }
        return selectionName.replace('-', delimiter);
    };
    const tooltip = () => i18n.t(`odds_option_tg_${selectionName.replace('-', '')}_tooltip`);
    return {
        name: displayName(),
        tooltip: tooltip(),
    };
}
